import { useRef } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  animateToBottom?: boolean;
  icon?: IconProp;
  successText: string;
  style?: React.CSSProperties;
  text: string;
  tooltip?: string;
  value: string;
}

const CopyButtonText = ({
  animateToBottom = false,
  icon = ['far', 'copy'],
  successText,
  style,
  text,
  tooltip,
  value
}: Props) => {
  const { theme } = useReduxState();

  const button = useRef<HTMLButtonElement>(undefined);

  const copy = () => {
    // check if we are inside modal
    const containerEl = getModalContainer(button.current) || document.body;

    // create temp textarea to copy text from
    const input = document.createElement('input');
    input.style.position = 'fixed';
    input.style.top = '-1000px';
    input.style.left = '-1000px';
    input.value = value;
    containerEl.appendChild(input);
    input.select();

    try {
      document.execCommand('copy');

      const copySuccess = button.current.querySelector(
        '.copy-button-success'
      ) as HTMLButtonElement;
      copySuccess.style.animation = 'none';
      copySuccess.offsetHeight;
      copySuccess.style.animation = '';
    } catch (err) {}

    containerEl.removeChild(input);
  };

  const getModalContainer = (initialEl: HTMLElement) => {
    let el = initialEl;
    while (el && el.classList && !el.classList.contains('modal-container')) {
      el = el.parentElement;
    }
    if (el && el.classList && el.classList.contains('modal-container')) {
      return el;
    }
    return null;
  };

  return (
    <button
      className="copy-button"
      onClick={copy}
      ref={button}
      style={style}
      type="button"
      {...(tooltip ? { 'data-tooltip': tooltip } : {})}
    >
      {text}
      {icon ? (
        <Icon icon={icon} style={{ fontSize: '1.2rem', marginLeft: 5 }} />
      ) : null}

      <div className="copy-button-success" style={{ animation: 'none' }}>
        {successText}
      </div>

      <style jsx>{`
        .copy-button {
          background-color: transparent;
          border: 0;
          color: ${theme.textColorLighter};
          cursor: pointer;
          font-family: inherit;
          font-size: 1.2rem;
          font-weight: 600;
          position: relative;
          transition: color 0.3s;
        }
        .copy-button:active,
        .copy-button:hover {
          color: ${theme.textColor};
        }
        @keyframes copysuccess {
          from {
            opacity: 0;
            top: 0;
          }
          to {
            opacity: 1;
            top: ${animateToBottom ? '20px' : '-20px'};
          }
        }
        .copy-button-success {
          animation: copysuccess 0.5s;
          color: ${theme.successStandaloneColor};
          left: -5px;
          opacity: 0;
          position: absolute;
          text-align: center;
          top: 0;
          width: 100%;
        }
      `}</style>
    </button>
  );
};

export default CopyButtonText;
